<template>

<section class='collection' :style='{
    "--collection-title-left-offset": titleLeftOffset,
    "--collection-title-scale": titleScale
}'>
    <div class='collection__grid grid'>
        <div class='collection__grid__col grid__col'>
            <div ref='content' class='collection__content'>

                <div v-if='isShowHeading' class='collection__heading' :class='{"collection__heading--is-title-case": isTitleCase}' :style="!collection ? { opacity: 0 } : {}">
                    <h2 ref='title'
                        class='collection__heading__title collection__heading__title--1'
                        :style='`width: ${contentWidth}px`'
                        v-text='collection?.title || "Collection"'
                    />

                    <!-- duplicate title for glow; html gets set after text fitting -->
                    <h2 class='collection__heading__title collection__heading__title--2'
                        :style='`width: ${contentWidth}px`'
                        v-html='titleHtml || ""'
                    />
                </div>

                <transition name="vue-fade">
                    <Spinner
                        v-if='!collection'
                        :variants='["large", "absolute"]'
                    />

                    <div v-else class='collection__items'>
                        <ProductThumb
                            v-for='(item, i) in products'
                            :key='item.id'
                            :handle='item.handle'
                            :media='item.media'
                            :title='item.title'
                            :available='item.available'
                            :price='item.price'
                            :id='item.id'
                            :isNotShowAsSoldOut='productsToNotShowAsSoldout.some(p => p.id === item.id)'
                            :cta='productsCta[item.id]' />
                    </div>
                </transition>

                <div v-if='hasMorePages || isLoadingMore' ref='loadMoreSentinel' class='collection__loading-more'>
                    <Spinner v-if='isLoadingMore' :variants='["small"]' />
                </div>


            </div>


        </div>
    </div>
</section>

</template>

<script>
import { getCssValueInPx } from '^/utils/markup'
import textFit from 'textfit'
import Spinner from '^/components/elements/Spinner.vue'
import ProductThumb from '^/components/elements/ProductThumb.vue'

export default {
    name: 'CollectionTier',
    props: {
        slug: {
            type: String,
            required: true
        },
        isShowHeading: {
            type: Boolean,
            default: false
        }
    },
    data: () => ({
        collection: null,
        products: [],
        productsCta: {},
        productsToNotShowAsSoldout: [],

        // pagination state
        currentPage: 1,
        totalPages: 1,
        isLoadingMore: false,

        isFontsLoaded: document.fonts.check("12px 'PP Fragment Variable Web'"),
        isTitleCase: true,
        contentWidth: 0,
        titleLeftOffset: '-0.06em',
        titleRightOffset: '-0.06em',
        titleScale: 1,
        titleHtml: '',
        resizeObserver: null,
        intersectionObserver: null
    }),
    computed: {
        hasMorePages () {
            return this.currentPage < this.totalPages
        }
    },
    watch: {
        isFontsLoaded (isFontsLoaded) {
            this.fitTitleText()
        },
        collection () {
            if (this.collection?.title?.toLowerCase().charAt(0) === 'a') {
                this.titleLeftOffset = '0em'
            }

            this.fitTitleText()
        }
    },
    created () {
        this.fetchPage(1)

        if (!this.isFontsLoaded) {
            document.fonts.ready.then(() => this.isFontsLoaded = true);
        }
    },
    mounted() {
        this.resizeObserver = new ResizeObserver(this.onResize)
        this.resizeObserver.observe(this.$refs.content)
    },
    updated() {
        // this.fitTitleText()
        this.setupIntersectionObserver()
    },
    beforeUnmount() {
        this.resizeObserver?.disconnect()
        this.intersectionObserver?.disconnect()
    },
    methods: {
        fetchPage (page) {
            const isFirstPage = page === 1

            if (!isFirstPage) {
                this.isLoadingMore = true
            }

            return fetch(`/collections/${this.slug}?view=json&page=${page}`)
                .then(response => response.json())
                .then(data => {
                    // pages arrive incrementally, so merge every page's cta map
                    this.productsCta = { ...this.productsCta, ...(data.products_cta || {}) }

                    if (isFirstPage) {
                        this.collection = data.collection
                        this.products = data.products
                        this.productsToNotShowAsSoldout = data.products_to_not_show_as_soldout || []
                    } else {
                        // Append new products to existing array
                        this.products = [...this.products, ...data.products]
                    }

                    // Update pagination state
                    this.currentPage = data.pagination.current_page
                    this.totalPages = data.pagination.total_pages
                })
                .finally(() => {
                    this.isLoadingMore = false
                })
        },
        fetchNextPage () {
            if (this.isLoadingMore || !this.hasMorePages) return

            this.fetchPage(this.currentPage + 1)
        },
        setupIntersectionObserver () {
            const sentinel = this.$refs.loadMoreSentinel
            if (!sentinel || this.intersectionObserver) return

            this.intersectionObserver = new IntersectionObserver(
                (entries) => {
                    if (entries[0].isIntersecting) {
                        this.fetchNextPage()
                    }
                },
                {
                    // trigger when sentinel is within 2 viewport heights
                    rootMargin: '0px 0px 200% 0px'
                }
            )

            this.intersectionObserver.observe(sentinel)
        },
        onResize (e) {
            this.contentWidth = Math.floor(e[0]?.contentRect?.width || 0)
            this.fitTitleText()
        },
        fitTitleText () {
            const $title = this.$refs.title
            if (!$title || !this.isFontsLoaded || !this.collection) return

            this.titleScale = 1

            this.$nextTick(() => {
                textFit($title, {
                    // multiLine: true,
                    widthOnly: true,
                    minFontSize: 24,
                    maxFontSize: this.isTitleCase ? 400 : 300,
                    reProcess: true
                })

                this.titleHtml = $title.innerHTML

                // measure values for scaling the element via css to account for
                // the left and right edges of the text being inset from the element
                // when the font is large
                const leftOffset = getCssValueInPx(this.titleLeftOffset, $title)
                const rightOffset = getCssValueInPx(this.titleRightOffset, $title)
                this.titleScale = 1 - (leftOffset / this.contentWidth) - (rightOffset / this.contentWidth)
            })
        },
    },
    components: {
        Spinner,
        ProductThumb
    }
};
</script>
